import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { BUDDY_HABIT_REPORT, CHAPTER_LIST_DROPDOWN } from "../../routes/routes";
import { glassSelectStyles } from "../../constant/selectStyles";
import Loader from "../../components/common/Loader";
import { applyExcelThinBorders, styleExcelHeaderRow } from "../../utils/excelStyles";

const authUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch (error) {
    return {};
  }
};

const habitOpenOn = (habit, date) => {
  const frequency = Number(habit.frequency);
  const day = Number(habit.schedule_day);
  if (frequency === 2) return date.isoWeekday() === day;
  if (frequency === 3) return date.date() === Math.min(day || 31, date.daysInMonth());
  return true;
};

const initials = (name) => String(name || "")
  .split(" ")
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join("")
  .toUpperCase();

const BuddyHabitReport = () => {
  const user = authUser();
  const userType = Number(user.user_type);
  const isChapterAdmin = userType === 3;
  const mustChooseChapter = [1, 2, 6, 7].includes(userType);
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState(isChapterAdmin ? user.chapter_id : "");
  const [pairMonth, setPairMonth] = useState(moment().format("YYYY-MM"));
  const [report, setReport] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isChapterAdmin) return;
    axios.get(CHAPTER_LIST_DROPDOWN).then((res) => {
      if (res.status === 200 && res.data.success) setChapters(res.data.data || []);
    });
  }, [isChapterAdmin]);

  useEffect(() => {
    if (!isChapterAdmin && !chapterId) {
      setReport(null);
      return;
    }
    setLoading(true);
    axios.get(BUDDY_HABIT_REPORT, {
      params: {
        chapter_id: isChapterAdmin ? user.chapter_id : chapterId,
        pair_month: pairMonth,
      },
    }).then((res) => {
      if (res.status === 200 && res.data.success) {
        const data = res.data.data;
        setReport(data);
        const first = data.pairs?.[0]?.user_one?.id || null;
        setSelectedId((current) => {
          const stillThere = (data.pairs || []).some((pair) => pair.user_one.id === current || pair.user_two.id === current);
          return stillThere ? current : first;
        });
      }
    }).catch((err) => {
      toast.error(err.response?.data?.msg || "Unable to load the report");
    }).finally(() => setLoading(false));
  }, [chapterId, pairMonth, isChapterAdmin, user.chapter_id]);

  const month = useMemo(() => moment(pairMonth, "YYYY-MM"), [pairMonth]);
  const days = useMemo(() => {
    const count = month.daysInMonth();
    return Array.from({ length: count }, (_, index) => index + 1);
  }, [month]);
  const throughDay = month.isSame(moment(), "month") ? moment().date() : month.daysInMonth();

  const model = useMemo(() => {
    if (!report) return null;
    const habits = report.habits || [];
    const marks = report.marks || [];
    const markOf = (subjectId, habitId, date) => marks.find((mark) => (
      mark.subject_id === subjectId && mark.habit_id === habitId && mark.period_date === date
    ));
    const applicable = (habit, day, subjectId) => {
      const date = month.clone().date(day);
      if (date.isAfter(moment(), "day")) return false;
      const mark = markOf(subjectId, habit.id, date.format("YYYY-MM-DD"));
      if (mark && (Number(mark.mark_status) === 1 || Number(mark.mark_status) === 2)) return true;
      if (habit.created_at && date.isBefore(moment(habit.created_at), "day")) return false;
      return habitOpenOn(habit, date);
    };
    const scoreFor = (subjectId) => {
      let done = 0;
      let possible = 0;
      habits.forEach((habit) => {
        days.forEach((day) => {
          if (!applicable(habit, day, subjectId)) return;
          possible += 1;
          const mark = markOf(subjectId, habit.id, month.clone().date(day).format("YYYY-MM-DD"));
          if (mark && Number(mark.mark_status) === 1) done += 1;
        });
      });
      return { done, possible, percent: possible ? Math.round((done / possible) * 100) : 0 };
    };
    const markedDays = (markerId, subjectId) => {
      const dates = new Set(
        marks
          .filter((mark) => mark.marker_id === markerId && mark.subject_id === subjectId && Number(mark.mark_status) > 0)
          .map((mark) => mark.period_date)
      );
      return dates.size;
    };
    const meetingsFor = (userId) => {
      const rows = (report.meetings || []).filter((row) => row.user_id === userId);
      return {
        total: rows.length,
        attended: rows.filter((row) => Number(row.meeting_status) === 1).length,
      };
    };
    const oneToOneFor = (leftId, rightId) => {
      const rows = (report.one_to_ones || []).filter((row) => (
        (row.from_user_id === leftId && row.to_user_id === rightId)
        || (row.from_user_id === rightId && row.to_user_id === leftId)
      )).sort((a, b) => String(a.date).localeCompare(String(b.date)));
      return rows[0] || null;
    };
    const pairOf = (userId) => (report.pairs || []).find((pair) => pair.user_one.id === userId || pair.user_two.id === userId) || null;
    return { habits, markOf, scoreFor, markedDays, meetingsFor, oneToOneFor, pairOf };
  }, [report, month, days]);

  const selected = model && selectedId ? model.pairOf(selectedId) : null;
  const member = selected ? (selected.user_one.id === selectedId ? selected.user_one : selected.user_two) : null;
  const buddy = selected ? (selected.user_one.id === selectedId ? selected.user_two : selected.user_one) : null;
  const memberScore = member && model ? model.scoreFor(member.id) : null;
  const buddyScore = buddy && model ? model.scoreFor(buddy.id) : null;
  const oneToOne = member && buddy && model ? model.oneToOneFor(member.id, buddy.id) : null;
  const memberMeetings = member && model ? model.meetingsFor(member.id) : { total: 0, attended: 0 };
  const meetingLabel = !memberMeetings.total
    ? "No meetings"
    : memberMeetings.attended === memberMeetings.total
      ? "Active"
      : memberMeetings.attended === 0
        ? "Inactive"
        : "Partial";

  const compliance = useMemo(() => {
    if (!model || !report) return [];
    return (report.pairs || []).map((pair) => {
      const left = model.scoreFor(pair.user_one.id);
      const right = model.scoreFor(pair.user_two.id);
      const possible = left.possible + right.possible;
      const done = left.done + right.done;
      const percent = possible ? Math.round((done / possible) * 100) : 0;
      const oto = model.oneToOneFor(pair.user_one.id, pair.user_two.id);
      const leftMeet = model.meetingsFor(pair.user_one.id);
      const rightMeet = model.meetingsFor(pair.user_two.id);
      return {
        pair,
        percent,
        oneToOne: Boolean(oto),
        attended: leftMeet.attended + rightMeet.attended,
        meetings: leftMeet.total + rightMeet.total,
      };
    });
  }, [model, report]);

  const dayMark = (habit, date) => {
    if (!member || !model) return "future";
    const mark = model.markOf(member.id, habit.id, date.format("YYYY-MM-DD"));
    if (mark && Number(mark.mark_status) === 1) return "done";
    if (mark && Number(mark.mark_status) === 2) return "miss";
    if (habit.created_at && date.isBefore(moment(habit.created_at), "day")) return "off";
    if (!habitOpenOn(habit, date)) return "off";
    if (date.isAfter(moment(), "day")) return "future";
    return "future";
  };

  const exportExcel = async () => {
    if (!compliance.length) {
      toast.error("No data found!.");
      return;
    }
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet("Compliance");
    const columns = [
      { header: "S.No", key: "sno" },
      { header: "Pair", key: "pair" },
      { header: "Habits", key: "habits" },
      { header: "1-2-1 held", key: "oto" },
      { header: "Meetings", key: "meetings" },
    ];
    worksheet.columns = columns;
    styleExcelHeaderRow(worksheet.getRow(1), columns.length);
    compliance.forEach((row, index) => {
      worksheet.addRow({
        sno: index + 1,
        pair: `${row.pair.user_one.name} + ${row.pair.user_two.name}`,
        habits: `${row.percent}%`,
        oto: row.oneToOne ? "Yes" : "No",
        meetings: `${row.attended} of ${row.meetings}`,
      });
    });
    worksheet.columns.forEach((column) => {
      column.width = Math.max(18, String(column.header || "").length + 6);
    });
    applyExcelThinBorders(worksheet);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Buddy-habit-report-${pairMonth}.xlsx`);
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="row align-items-center">
          <div className="col-lg-8">
            <h5 className="page-title">Member habit calendar</h5>
            <ol className="breadcrumb">
              <li>
                <div className="breadcrumb_f">
                  <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                  </div>
                  <div><Link to="/dashboard"><span>Dashboard</span></Link></div>
                </div>
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
              </li>
              <li><span className="active_breadcrumb">Buddy Habit Report</span></li>
            </ol>
          </div>
          <div className="col-lg-4">
            <div className="pt-btn habit-cal-actions">
              <button type="button" className="btn rounded-pill habit-cal-back" onClick={() => document.getElementById("habit-compliance")?.scrollIntoView({ behavior: "smooth" })}>
                Back to report
              </button>
              <button type="button" className="btn rounded-pill btn-primary excel-btn ibutton" disabled={(!isChapterAdmin && !chapterId) || loading} onClick={exportExcel}>
                <span className="tf-icons bx bxs-spreadsheet"></span>
                &nbsp; Excel
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1 habit-cal-page">
        <div className="filter-card">
          <h6 className="filter-card-title">Search &amp; Filter</h6>
          <div className="row g-3 align-items-end">
            {mustChooseChapter && (
              <div className="col-12 col-md-4">
                <label className="filter-label">Chapter</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={glassSelectStyles}
                  options={chapters}
                  isClearable
                  placeholder="Select chapter"
                  value={chapters.find((item) => item.value === Number(chapterId)) || null}
                  onChange={(selectedOption) => setChapterId(selectedOption?.value || "")}
                />
              </div>
            )}
            <div className="col-12 col-md-3">
              <label className="filter-label">Month</label>
              <input type="month" className="form-control" value={pairMonth} onChange={(e) => setPairMonth(e.target.value || moment().format("YYYY-MM"))} />
            </div>
          </div>
        </div>

        {!isChapterAdmin && !chapterId ? (
          <div className="card icard sticky-table-card buddy-empty-stage">
            <table className="table itable">
              <tbody>
                <tr><td className="center">Select a chapter to see the habit report.</td></tr>
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="card icard sticky-table-card">
            <table className="table itable"><tbody><Loader loading colspan="1" /></tbody></table>
          </div>
        ) : !member ? (
          <div className="card icard sticky-table-card buddy-empty-stage">
            <table className="table itable">
              <tbody>
                <tr><td className="center">No buddy pairs for this month.</td></tr>
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <section className="habit-banner">
              <div className="habit-banner-person">
                <span>{initials(member.name)}</span>
                <div>
                  <small>{month.format("MMMM YYYY").toUpperCase()} · BUDDY OF {buddy.name.toUpperCase()}</small>
                  <strong>{member.name}</strong>
                  <em>{[member.company, member.category].filter(Boolean).join(" · ") || "Member"}</em>
                </div>
              </div>
              <div className="habit-banner-stat">
                <strong>{memberScore.percent}%</strong>
                <span>Overall</span>
              </div>
              <div className="habit-banner-stat">
                <strong>{oneToOne ? "Yes" : "No"}</strong>
                <span>{oneToOne ? moment(oneToOne.date).format("D MMM") : "1-2-1 held"}</span>
              </div>
              <div className="habit-banner-stat">
                <strong>{meetingLabel}</strong>
                <span>Meeting status</span>
              </div>
            </section>

            <section className="habit-sheet">
              <header className="habit-week-head">
                <div>
                  <h6>Month at a glance</h6>
                  <p>{month.format("MMMM YYYY")}</p>
                </div>
              </header>
              <div className="habit-grid-scroll">
                <table className="habit-grid">
                  <thead>
                    <tr>
                      <th />
                      {days.map((day) => <th key={day}>{day}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {model.habits.map((habit) => (
                      <tr key={habit.id}>
                        <th title={habit.name}>{habit.name}</th>
                        {days.map((day) => (
                          <td key={day}><i className={`habit-cell is-${dayMark(habit, month.clone().date(day))}`} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="habit-legend">
                <span><i className="habit-cell is-done" /> Done</span>
                <span><i className="habit-cell is-miss" /> Not done</span>
                <span><i className="habit-cell is-off" /> Not scheduled</span>
                <span><i className="habit-cell is-future" /> Not yet</span>
              </div>
            </section>

            <div className="habit-lower">
              <section className="habit-account">
                <h6>Mutual accountability</h6>
                <div>
                  <span>{buddy.name.split(" ")[0]} marked for {member.name.split(" ")[0]}</span>
                  <strong>{memberScore.done} of {memberScore.possible} done</strong>
                </div>
                <div>
                  <span>{member.name.split(" ")[0]} marked for {buddy.name.split(" ")[0]}</span>
                  <strong>{buddyScore.done} of {buddyScore.possible} done</strong>
                </div>
                <div>
                  <span>Days marked</span>
                  <strong>{model.markedDays(buddy.id, member.id)} of {throughDay}</strong>
                </div>
              </section>
              <section className="habit-compliance" id="habit-compliance">
                <header>
                  <div>
                    <h6>Monthly compliance report</h6>
                    <p>For this chapter and month</p>
                  </div>
                  <button type="button" className="habit-export" onClick={exportExcel}>Export Excel</button>
                </header>
                <table>
                  <thead>
                    <tr>
                      <th>Pair</th>
                      <th>Habits</th>
                      <th>1-2-1 held</th>
                      <th>Meetings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compliance.map((row) => (
                      <tr
                        key={row.pair.pair_id}
                        className={`${row.percent < 60 ? "is-low" : ""} ${row.pair.pair_id === selected.pair_id ? "is-on" : ""}`}
                        onClick={() => setSelectedId(row.pair.user_one.id)}
                      >
                        <td>{row.pair.user_one.name} + {row.pair.user_two.name}</td>
                        <td>{row.percent}%</td>
                        <td>{row.oneToOne ? "Yes" : "No"}</td>
                        <td>{row.attended} of {row.meetings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuddyHabitReport;
